import { useCurrentNetwork, useCurrentWallet, useDAppKit, UiWalletAccount } from '@mysten/dapp-kit-react'
import { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { formatAddress } from '@mysten/sui/utils'
import { useResolveSuiNSName } from 'utils/useResolveSuiNSName'
import { toast } from 'react-toastify'
import { usePostHog } from 'posthog-js/react'

import { Typography } from 'components/Typography/Typography'
import { IconCheck } from 'components/Icons/IconCheck'
import { IconWalletIndicator } from 'components/Icons/IconWalletIndicator'
import { useOnClickOutside } from 'utils/onClickOutside'
import { IconCopy } from 'components/Icons/IconCopy'
import { IconDisconnect } from 'components/Icons/IconDisconnect'
import { Menu, MenuItem } from 'components/Menu/Menu'
import { useViewAsWallet } from 'contexts/ViewAsWalletContext'
import { clearSentrySession, initializeSentrySession } from 'utils/sentry-utils'

interface AccountDropdownMenuProps {
  currentAccount: UiWalletAccount
}

interface AccountDropdownMenuItemProps {
  account: UiWalletAccount
  active?: boolean
  onToggleMenu: () => void
}

export function AccountDropdownMenuItem({ account, active, onToggleMenu }: AccountDropdownMenuItemProps) {
  const dAppKit = useDAppKit()
  const { data: domain } = useResolveSuiNSName(account.label ? null : account.address)

  const network = useCurrentNetwork()
  const currentWallet = useCurrentWallet()

  const handleAccountClick = useCallback(() => {
    dAppKit.switchAccount({ account })
    onToggleMenu()
    initializeSentrySession(account.address, network, currentWallet?.name)
  }, [account, onToggleMenu, dAppKit, network, currentWallet?.name])

  const handleCopyClick = useCallback(
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation()
      await navigator.clipboard.writeText(account?.address || '')
      toast.success('Address copied')
    },
    [account?.address]
  )
  return (
    <MenuItem onClick={handleAccountClick}>
      <Typography
        variant="description"
        fontWeight="semiBold"
        style={{ maxWidth: 200, display: 'flex', alignItems: 'center' }}
      >
        {account.label ?? domain ?? formatAddress(account.address)}
        <div style={{ display: 'flex', marginLeft: 8 }} onClick={handleCopyClick}>
          <IconCopy />
        </div>
      </Typography>
      {active ? <IconCheck /> : null}
    </MenuItem>
  )
}

const AccountDropdownMenuContainer = styled.div`
  position: relative;
`

const Disconnect = styled(Typography)`
  padding: 16px;
  cursor: pointer;
  border-radius: 8px;
`

const DisconnectTextWrapper = styled.div`
  padding: 12px 24px;
  border: 1px solid ${p => p.theme.colors.text.primary};
  border-radius: 100px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  & > svg {
    margin-left: 8px;
  }
  transition: box-shadow 0.3s ease;
  &:hover {
    ${p => `box-shadow: 0 4px 10px ${p.theme.colors.button.outlinedBoxShadow};`}
  }
`

const Divider = styled.div`
  margin: 0 16px;
  border-top: 1px solid ${p => p.theme.colors.divider};
`

const DropdownMenuTrigger = styled.div<{ $disabled: boolean }>`
  padding: 11px 24px;
  border: 0.7px solid ${p => p.theme.colors.text.primary};
  border-radius: 100px;
  cursor: pointer;
  svg {
    margin-left: 10px;
  }

  transition: box-shadow 0.3s ease;
  &:hover {
    ${p => `box-shadow: 0 4px 10px ${p.theme.colors.button.outlinedBoxShadow};`}
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
    padding: 8px 16px;
  }
  ${p => p.$disabled && `opacity: 0.3; pointer-events: none;`}
`

const AccountMenuTitle = styled(Typography)`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }
`

export const AccountDropdownMenu = ({ currentAccount }: AccountDropdownMenuProps) => {
  const { viewAsWalletAddress } = useViewAsWallet()
  const posthog = usePostHog()

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const dAppKit = useDAppKit()

  const { data: domain } = useResolveSuiNSName(currentAccount.label ? null : currentAccount.address)
  const accounts = useCurrentWallet()?.accounts ?? []

  const toggleMenu = useCallback(() => {
    // Do nothing when view as address is set
    if (viewAsWalletAddress) {
      return
    }
    setMenuOpen(!isMenuOpen)
  }, [isMenuOpen, viewAsWalletAddress])

  const handleDisconnectClick = useCallback(() => {
    clearSentrySession()
    // If we target specific users, featured flag won't be reset unless we manually do it after disconnect
    posthog?.reset()
    posthog?.reloadFeatureFlags()
    void dAppKit.disconnectWallet()
  }, [dAppKit, posthog])

  return (
    <AccountDropdownMenuContainer>
      <DropdownMenuTrigger onClick={toggleMenu} $disabled={!!viewAsWalletAddress}>
        <AccountMenuTitle variant="description" fontWeight="semiBold" style={{ display: 'flex', alignItems: 'center' }}>
          {currentAccount.label ?? domain ?? formatAddress(currentAccount.address)}
          <IconWalletIndicator />
        </AccountMenuTitle>
      </DropdownMenuTrigger>

      {isMenuOpen && (
        <Menu anchorElement={menuNode} style={{ width: 250 }}>
          {accounts.map(account => (
            <AccountDropdownMenuItem
              key={account.address}
              account={account}
              active={currentAccount.address === account.address}
              onToggleMenu={toggleMenu}
            />
          ))}
          <Divider />
          <div onClick={handleDisconnectClick}>
            <Disconnect variant="description" fontWeight="medium">
              <DisconnectTextWrapper>
                Disconnect <IconDisconnect />
              </DisconnectTextWrapper>
            </Disconnect>
          </div>
        </Menu>
      )}
    </AccountDropdownMenuContainer>
  )
}
